import { ThemeOptions } from '@mui/material/styles';

export const getTheme = (mode: 'light' | 'dark'): ThemeOptions => ({
  direction: 'ltr',
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: { main: '#2196f3', light: '#e3f2fd', dark: '#1e88e5' },
          secondary: { main: '#673ab7', light: '#ede7f6', dark: '#5e35b1' },
          success: { main: '#00e676', light: '#b9f6ca', dark: '#00c853', contrastText: '#ffffff' },
          info: { main: '#2196f3', light: '#e3f2fd', dark: '#1e88e5', contrastText: '#ffffff' },
          error: { main: '#f44336', light: '#ef9a9a', dark: '#c62828', contrastText: '#ffffff' },
          warning: { main: '#ffe57f', light: '#fff8e1', dark: '#ffc107', contrastText: '#212121' },
          grey: {
            50: '#f8fafc',
            100: '#eef2f6',
            200: '#e3e8ef',
            300: '#cdd5df',
            500: '#697586',
            600: '#4b5565',
            700: '#364152',
            900: '#121926',
          },
          text: { primary: '#364152', secondary: '#697586' },
          background: { default: '#f8fafc', paper: '#ffffff' },
          divider: '#e3e8ef',
          action: { disabledBackground: 'rgba(73,82,88,0.12)', hoverOpacity: 0.02, hover: '#f8fafc' },
        }
      : {
          primary: { main: '#2196f3', light: '#1a3a5c', dark: '#1e88e5' },
          secondary: { main: '#7c4dff', light: '#2a1f5e', dark: '#651fff' },
          success: { main: '#00e676', light: '#1b3c48', dark: '#00c853', contrastText: '#ffffff' },
          info: { main: '#2196f3', light: '#1a3a5c', dark: '#1e88e5', contrastText: '#ffffff' },
          error: { main: '#f44336', light: '#4b313d', dark: '#c62828', contrastText: '#ffffff' },
          warning: { main: '#ffe57f', light: '#4d3a2a', dark: '#ffc107', contrastText: '#212121' },
          grey: {
            50: '#f8fafc',
            100: '#eef2f6',
            200: '#e3e8ef',
            300: '#cdd5df',
            500: '#697586',
            600: '#4b5565',
            700: '#364152',
            900: '#121926',
          },
          text: { primary: '#e3e8ef', secondary: '#697586' },
          background: { default: '#1a223f', paper: '#111936' },
          divider: '#364152',
          action: { disabledBackground: 'rgba(73,82,88,0.12)', hoverOpacity: 0.04, hover: '#1e2a4a' },
        }),
  },
  typography: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    h1: { fontWeight: 700, fontSize: '2.125rem' },
    h2: { fontWeight: 700, fontSize: '1.5rem' },
    h3: { fontWeight: 600, fontSize: '1.25rem' },
    h4: { fontWeight: 600, fontSize: '1rem' },
    h5: { fontWeight: 500, fontSize: '0.875rem' },
    h6: { fontWeight: 500, fontSize: '0.75rem' },
    button: { textTransform: 'capitalize' },
    body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: '1.334em' },
    body2: { fontSize: '0.75rem', fontWeight: 400, lineHeight: '1.5em' },
    subtitle1: { fontSize: '0.875rem', fontWeight: 500 },
    subtitle2: { fontSize: '0.75rem', fontWeight: 400 },
    caption: { fontSize: '0.75rem', fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': { boxSizing: 'border-box' },
        body: { transition: 'background-color 0.3s ease, color 0.3s ease' },
        '.MuiPaper-elevation9, .MuiPopover-root .MuiPaper-elevation': {
          boxShadow:
            'rgb(145 158 171 / 30%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 12px 24px -4px !important',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 12, boxShadow: '0px 2px 8px rgba(0,0,0,0.08)' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          marginBottom: 4,
          '&.Mui-selected': {
            backgroundColor: mode === 'light' ? '#e3f2fd' : '#1a3a5c',
            color: '#2196f3',
            '& .MuiListItemIcon-root': { color: '#2196f3' },
            '&:hover': {
              backgroundColor: mode === 'light' ? '#e3f2fd' : '#1a3a5c',
            },
          },
          '&:hover': {
            backgroundColor: mode === 'light' ? '#eef2f6' : '#1e2a4a',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: mode === 'light' ? '#e3f2fd' : '#1a3a5c',
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: mode === 'light' ? '#bbdefb' : '#1565c0',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: mode === 'light' ? '#e3e8ef' : '#364152',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none !important',
          boxShadow: 'none',
          backgroundColor: mode === 'light' ? '#f8fafc' : '#1a223f',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { boxShadow: 'none' },
      },
    },
  },
});

const CustomTheme = getTheme('light');
export default CustomTheme;
